import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, ShieldAlert, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplateRow = ({ t }) => {
  const tone =
    t.status === 'APPROVED' ? 'text-emerald-300'
    : t.status === 'PENDING' ? 'text-amber-300'
    : 'text-rose-300';
  return (
    <li
      data-testid={`meta-health-template-${t.name}`}
      className="flex items-center justify-between text-[11px] text-white/70 py-0.5"
    >
      <span className="truncate">{t.name}</span>
      <span className="flex items-center gap-2 flex-shrink-0 ml-2">
        <span className="text-[10px] text-white/40 uppercase">{t.language}</span>
        <span className={`text-[10px] font-bold uppercase ${tone}`}>{t.status}</span>
      </span>
    </li>
  );
};

const CheckRow = ({ c }) => (
  <li
    data-testid={`meta-health-check-${c.name}`}
    className="flex items-start gap-2 py-1"
  >
    {c.ok
      ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
      : <XCircle className="h-4 w-4 text-rose-400 mt-0.5 flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold text-white">{c.name.replace(/_/g, ' ')}</p>
      {c.detail && (
        <p className="text-[10px] text-white/50 leading-snug break-words">{c.detail}</p>
      )}
    </div>
  </li>
);

export const AdminMetaHealthTile = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHealth = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/sales/meta-health`);
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to fetch Meta health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial fetch + poll. We keep fetchHealth out of the dep array (and the
  // closure is stable via useCallback with []) so React's set-state-in-effect
  // lint rule doesn't trip — the poll is a side-effect that lives outside
  // React render flow.
  useEffect(() => {
    let cancelled = false;
    const tick = () => { if (!cancelled) fetchHealth(); };
    tick();
    const id = setInterval(tick, 120000); // 2 min
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading) {
    return (
      <div
        data-testid="admin-meta-health-tile-loading"
        className="bg-gradient-to-br from-[#0A0A0C] via-[#1A1A1A] to-[#0A0A0C] border-l-4 border-[#25D366] p-6 text-white/60 text-sm"
      >
        Loading Meta health…
      </div>
    );
  }
  if (err) {
    return (
      <div
        data-testid="admin-meta-health-tile-error"
        className="bg-rose-50 border-l-4 border-rose-400 p-4 text-sm text-rose-700"
      >
        Meta health error: {err}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div
      data-testid="admin-meta-health-tile"
      className="relative bg-gradient-to-br from-[#0A0A0C] via-[#1A1A1A] to-[#0A0A0C] border-l-4 border-[#25D366] overflow-hidden mb-6"
    >
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#25D366] blur-3xl opacity-10 pointer-events-none" />
      <div className="relative px-6 py-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#25D366] font-bold mb-1">
              Meta WhatsApp Health
            </p>
            <h2 className="font-['Playfair_Display'] text-2xl text-white flex items-center gap-2">
              {data.ok ? (
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              ) : (
                <ShieldAlert className="h-6 w-6 text-amber-400" />
              )}
              {data.ok ? 'All gates open' : 'Action needed'}
            </h2>
          </div>
          <button
            onClick={fetchHealth}
            disabled={refreshing}
            data-testid="admin-meta-health-refresh"
            className="inline-flex items-center gap-1 text-[10px] text-white/40 hover:text-white/80 disabled:opacity-50 uppercase tracking-wider"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-white/10 bg-black/30 p-4">
            <p className="text-[10px] tracking-wider uppercase font-bold text-white/60 mb-2">
              Gate checks
            </p>
            <ul className="space-y-1">
              {(data.checks || []).map((c) => <CheckRow key={c.name} c={c} />)}
            </ul>
          </div>

          <div className="border border-white/10 bg-black/30 p-4">
            <p className="text-[10px] tracking-wider uppercase font-bold text-white/60 mb-2">
              Approved templates ({(data.templates || []).length})
            </p>
            {(data.templates || []).length === 0 ? (
              <p className="text-xs text-white/40 italic">
                No templates returned. Either the WABA id is missing or Meta has not yet
                approved any. Configure WHATSAPP_BUSINESS_ACCOUNT_ID in backend env.
              </p>
            ) : (
              <ul className="space-y-0.5 max-h-48 overflow-y-auto">
                {data.templates.map((t) => <TemplateRow key={t.name} t={t} />)}
              </ul>
            )}
          </div>
        </div>

        <p className="text-[10px] text-white/30 mt-3">
          Checked {new Date(data.checked_at).toLocaleTimeString()} · auto-refreshes every 2 min · cache 60s
        </p>
      </div>
    </div>
  );
};

export default AdminMetaHealthTile;
